/**
 * trackCriteriaVersions — a track's criteria as numbered versions (migration
 * 1005, roadmap 1C V-11; canon Doc 01 "Criteria Set": a versioned collection of
 * criteria and scales).
 *
 * Every lock of a track's Criteria & Scales writes the next version: each
 * criterion's key, the label the workspace gives it, and its scale, with who
 * locked it and when. Reopening closes the open version with who, when and why;
 * the version itself never changes again. Once a session on the track ran on the
 * open version, reopening needs a written reason
 * (set_track_criteria_and_ranges_locked refuses with `reason_required`).
 *
 * Also the client's read of who may change them — the server's rules
 * (internal_track_lock_authority, 711, for the track's locks;
 * internal_track_criteria_may_change, 1006, for adding and removing a criterion),
 * so a control is not drawn for a person the server would refuse (build contract
 * §2.8). The server stays the wall.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';

export interface TrackCriterionAsLocked {
  attributeKey: string;
  label: string;
  rangeStatus: string | null;
  rangeValue: unknown;
  /** The criterion's weight when locked (1047); null when none was set or the version predates weights. */
  weight: number | null;
}

export interface TrackCriteriaVersion {
  id: string;
  version: number;
  criteria: TrackCriterionAsLocked[];
  lockedAt: Date;
  lockedBy: string | null;
  reopenedAt: Date | null;
  reopenedBy: string | null;
  reopenReason: string | null;
  /** Sessions on the track that ran (RUN_COMPLETE / PUBLISHED) while this version was locked. */
  sessionsRun: number;
}

interface VersionRow {
  id: string;
  version: number;
  criteria: unknown;
  locked_at: string;
  locked_by: string | null;
  reopened_at: string | null;
  reopened_by: string | null;
  reopen_reason: string | null;
  sessions_run: number | null;
}

function criteriaFrom(raw: unknown): TrackCriterionAsLocked[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((c) => {
    if (!c || typeof c !== 'object') return [];
    const r = c as Record<string, unknown>;
    const key = typeof r.attribute_key === 'string' ? r.attribute_key : '';
    if (!key) return [];
    return [{
      attributeKey: key,
      label: typeof r.label === 'string' && r.label.trim() ? r.label : key,
      rangeStatus: typeof r.range_status === 'string' ? r.range_status : null,
      rangeValue: r.range_value ?? null,
      weight: typeof r.weight === 'number' && Number.isFinite(r.weight) ? r.weight : null,
    }];
  });
}

export function versionFromRow(row: VersionRow): TrackCriteriaVersion {
  return {
    id: row.id,
    version: row.version,
    criteria: criteriaFrom(row.criteria),
    lockedAt: new Date(row.locked_at),
    lockedBy: row.locked_by ?? null,
    reopenedAt: row.reopened_at ? new Date(row.reopened_at) : null,
    reopenedBy: row.reopened_by ?? null,
    reopenReason: row.reopen_reason ?? null,
    sessionsRun: row.sessions_run ?? 0,
  };
}

/** Every version of the track's criteria, newest first (get_track_criteria_versions). */
export async function fetchTrackCriteriaVersions(
  trackId: string,
): Promise<{ versions: TrackCriteriaVersion[]; error?: string }> {
  const { data, error } = await getTypedSupabaseClient().rpc('get_track_criteria_versions', { p_track_id: trackId });
  if (error) return { versions: [], error: error.message };
  return { versions: ((data ?? []) as VersionRow[]).map(versionFromRow) };
}

/** The version the lock holds open now — none before the first lock or after a reopen. */
export function openVersion(versions: readonly TrackCriteriaVersion[]): TrackCriteriaVersion | null {
  return versions.find((v) => v.reopenedAt === null) ?? null;
}

/** Reopening a version a session ran on needs a written reason (the server's rule, 1005). */
export function reopenNeedsReason(open: TrackCriteriaVersion | null): boolean {
  return (open?.sessionsRun ?? 0) > 0;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "1 session ran on it" / "No session has run on it yet". */
export function sessionsRunLine(n: number): string {
  return n > 0 ? `${plural(n, 'session')} ran on it` : 'No session has run on it yet';
}

/** What reopening will do, in the words the reopen dialog shows. */
export function reopenConsequence(open: TrackCriteriaVersion): string {
  return `${plural(open.sessionsRun, 'session')} ran on version ${open.version}. ` +
    `Version ${open.version} is kept as it was locked, and those sessions keep it. ` +
    `Your changes become version ${open.version + 1} when you lock again.`;
}

// ─── Who may change them ─────────────────────────────────────────────────────

export interface TrackLockActor {
  userId: string | null | undefined;
  /** Workspace owner/admin, or platform admin. */
  isWorkspaceAdmin: boolean;
  /** Holds one of the grants internal_track_lock_authority reads. */
  holdsLockRight: boolean;
}

/** The grants internal_track_lock_authority (711) accepts, for `can(...)`. */
export const TRACK_LOCK_RIGHTS = ['pools.lock', 'values.define_range', 'values.approve_range', 'tracks.configure'] as const;

export const TRACK_LOCK_AUTHORITY_REASON =
  'Only the cycle owner, a workspace admin, or someone who may lock pools, define or approve value ranges, or configure tracks can change this lock.';

export const TRACK_CRITERIA_AUTHORITY_REASON =
  "Choosing a track's criteria takes the track owner, the cycle owner, a workspace admin, or someone who may select criteria.";

function signedIn(userId: string | null | undefined): userId is string {
  return !!userId && userId !== 'anonymous' && userId !== 'system';
}

const same = (a: string | null | undefined, b: string): boolean => !!a && a.toLowerCase() === b.toLowerCase();

/** Mirrors internal_track_lock_authority: the cycle's owner_id, a workspace admin, or a lock grant. */
export function canChangeTrackLock(
  actor: TrackLockActor,
  cycleOwnerId: string | null | undefined,
): { allowed: boolean; reason?: string } {
  if (!signedIn(actor.userId)) return { allowed: false, reason: 'Sign in to change this lock.' };
  if (actor.isWorkspaceAdmin || actor.holdsLockRight) return { allowed: true };
  if (same(cycleOwnerId, actor.userId)) return { allowed: true };
  return { allowed: false, reason: TRACK_LOCK_AUTHORITY_REASON };
}

/**
 * Mirrors internal_track_criteria_may_change (1006): may this person add or remove
 * a criterion on the track? Holds criteria.select (a workspace admin does), owns the
 * track's cycle (owner_id, else created_by), or is the track's named owner.
 */
export function canChangeTrackCriteria(
  actor: { userId: string | null | undefined; isWorkspaceAdmin: boolean; holdsCriteriaRight: boolean },
  ownership: { cycleOwnerId?: string | null; cycleCreatedBy?: string | null; trackOwnerId?: string | null },
): { allowed: boolean; reason?: string } {
  if (!signedIn(actor.userId)) return { allowed: false, reason: "Sign in to change this track's criteria." };
  if (actor.isWorkspaceAdmin || actor.holdsCriteriaRight) return { allowed: true };
  const ownsCycle = ownership.cycleOwnerId
    ? same(ownership.cycleOwnerId, actor.userId)
    : same(ownership.cycleCreatedBy, actor.userId);
  if (ownsCycle || same(ownership.trackOwnerId, actor.userId)) return { allowed: true };
  return { allowed: false, reason: TRACK_CRITERIA_AUTHORITY_REASON };
}
